import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Hoot from "./Hoot";
import { RiLockPasswordLine } from "react-icons/ri";
import { PiSignInBold } from "react-icons/pi";
import { FaRegEnvelope } from "react-icons/fa6";

axios.defaults.withCredentials = true;

const loginStyles = `
:root{
  --primary:#6052ff;
  --bg:#f5f5ff;
  --card:#ffffff;
  --muted:#6b7280;
  --text:#111827;
  --border:#e5e7eb;
  --danger:#b91c1c;
  --success:#15803d;
}
*{box-sizing:border-box;}
html,body{height:100%;}
body{
  font-family:"Montserrat",system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
  color:var(--text);
}

.auth-page-wrapper{
  min-height:100vh;
  background:radial-gradient(circle at top,#e0ddff 0,#f9fafb 55%);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:16px;
}
.login-shell{
  max-width:900px;
  width:100%;
  display:grid;
  grid-template-columns: minmax(0,1.1fr) minmax(0,1.1fr);
  gap:24px;
}
@media (max-width:768px){
  .login-shell{ grid-template-columns:1fr; }
}

/* HERO */
.login-hero{
  padding:16px 10px 16px 0;
  display:flex;
  flex-direction:column;
  justify-content:center;
  min-height:220px;
}
.badge-pill{
  display:inline-flex;
  align-items:center;
  gap:.35rem;
  padding:.25rem .65rem;
  border-radius:999px;
  background:#eef2ff;
  color:#4338ca;
  font-size:.75rem;
  font-weight:700;
  border:1px solid #e0e7ff;
}
.hero-title{
  font-size:1.7rem;
  font-weight:900;
  line-height:1.2;
  margin:12px 0 6px;
  color:var(--text);
}
.hero-subtitle{
  font-size:.92rem;
  color:var(--muted);
  max-width:420px;
}
.hero-pill-row{
  display:flex;
  flex-wrap:wrap;
  gap:6px;
  margin-top:12px;
  font-size:.75rem;
  color:var(--muted);
}
.hero-pill{
  border-radius:999px;
  background:#f3f4ff;
  padding:.2rem .6rem;
  border:1px solid #e5e7ff;
}
.small-info{
  font-size:.78rem;
  color:var(--muted);
  margin-top:12px;
}

/* CARD LOGIN */
.login-card{
  background:var(--card);
  border-radius:18px;
  border:1px solid var(--border);
  padding:18px 18px 16px;
  box-shadow:0 16px 45px rgba(15,23,42,.10);
}
.login-brand{
  display:flex;
  align-items:center;
  gap:10px;
  margin-bottom:10px;
}
.login-brand-icon{
  width:32px;height:32px;border-radius:10px;
  background:linear-gradient(135deg,var(--primary),#7c3aed);
  display:inline-grid;place-items:center;
  color:#f9fafb;font-weight:900;font-size:.9rem;
}
.login-brand-text{
  font-weight:900;
  font-size:1rem;
  color:var(--text);
}
.login-muted{
  font-size:.82rem;
  color:var(--muted);
}

/* FORM */
.form-label{
  font-size:.78rem !important;
  font-weight:700 !important;
  color:var(--text) !important;
}
.form-control{
  background:#ffffff !important;
  border-radius:999px !important;
  border:1px solid var(--border) !important;
  font-size:.88rem !important;
  color:var(--text) !important;
}
.form-control:focus{
  border-color:var(--primary) !important;
  box-shadow:0 0 0 1px rgba(96,82,255,.35) !important;
  background:#ffffff !important;
}

/* BOTÃO */
.btn-primary{
  border-radius:999px !important;
  border:none !important;
  font-size:.92rem !important;
  font-weight:800 !important;
  padding:.5rem 1rem !important;
  background:linear-gradient(135deg,var(--primary),#7c3aed) !important;
}
.btn-primary:hover{ filter:brightness(1.05); }

.link-muted{
  color:var(--muted);
  font-size:.82rem;
}
.link-muted a{
  color:#4f46e5;
  text-decoration:none;
  font-weight:700;
}
.link-muted a:hover{text-decoration:underline;}

.input-group-text{
  background:#ffffff !important;
  border-radius:999px 0 0 999px !important;
  border:1px solid var(--border) !important;
  border-right:0 !important;
  color:#9ca3af !important;
}
.form-control.round-right{
  border-radius:0 999px 999px 0 !important;
  border-left:0 !important;
}

/* MENSAGENS */
.error-text{
  color:var(--danger);
  font-size:.82rem;
  font-weight:800;
  background:rgba(185, 28, 28, 0.06);
  border:1px solid rgba(185, 28, 28, 0.18);
  padding:10px 12px;
  border-radius:14px;
}
.success-text{
  color:var(--success);
  font-size:.82rem;
  font-weight:800;
  background:rgba(21, 128, 61, 0.06);
  border:1px solid rgba(21, 128, 61, 0.18);
  padding:10px 12px;
  border-radius:14px;
}
.security-note{
  font-size:.75rem;
  color:var(--muted);
  margin-top:10px;
  line-height:1.35;
}
.security-note b{ color: var(--text); }

`;

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Importante: mensagens genéricas (evita “dar pistas” a utilizadores desconhecidos)
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const navigate = useNavigate();

  // Verificar se o utilizador já tem sessão
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        await axios.get(Hoot() + "me", { withCredentials: true });
        setIsLoggedIn(true);
        navigate("/dashboard");
      } catch (err) {
        // Não mostramos detalhes técnicos no ecrã de login
        console.log(err);
        setIsLoggedIn(false);
      }
    };

    checkLoginStatus();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post(
        Hoot() + "login",
        { email, password },
        { withCredentials: true }
      );

      setSuccess("Sessão iniciada. A redirecionar…");
      console.clear();
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (err) {
      console.log(err);

      // Mensagem SEMPRE genérica para não permitir “enumeração” (email existe/não existe, etc.)
      // (Se quiseres, podes tratar apenas erros de rede separadamente.)
      const isNetwork = !err?.response;
      setError(
        isNetwork
          ? "Não foi possível ligar ao servidor. Tenta novamente."
          : "Não foi possível iniciar sessão. Verifica as credenciais e tenta novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit(e);
  };

  if (isLoggedIn) return null;

  return (
    <>
      <style>{loginStyles}</style>

      <div className="auth-page-wrapper">
        <div className="login-shell">
          {/* HERO (texto menos “exposto” a desconhecidos) */}
          <div className="login-hero">
            <div>
              <span className="badge-pill">Área Reservada • Eduall</span>

              <h1 className="hero-title">
                Aceder ao{" "}
                <span style={{ color: "#4f46e5" }}>painel de gestão</span>.
              </h1>

              <p className="hero-subtitle">
                Este acesso é destinado a utilizadores autorizados. Introduz as tuas credenciais
                para entrar com segurança.
              </p>

              <div className="hero-pill-row">
                <span className="hero-pill">Acesso protegido</span>
                <span className="hero-pill">Sessão segura</span>
                <span className="hero-pill">Gestão centralizada</span>
              </div>
            </div>

            <div className="small-info">
              Se não tens acesso, contacta o responsável pelo sistema.
            </div>
          </div>

          {/* CARD LOGIN */}
          <div className="login-card">
            <div className="login-brand">
              <div className="login-brand-icon">
              <img 
              src="https://ik.imagekit.io/zks5iegia/image-gen__2___1_-removebg-preview%20(1).png" 
               style={{width:"30px",height:"30px",borderRadius:"4px"}} alt="" /></div>
              <div>
                <div className="login-brand-text">Iniciar sessão</div>
                <div className="login-muted">
                  Apenas para utilizadores com permissões atribuídas.
                </div>
              </div>
            </div>

            <Form className="mt-2" onSubmit={handleSubmit}>
              {error && <div className="mb-2 error-text">{error}</div>}
              {success && <div className="mb-2 success-text">{success}</div>}

              <Form.Group className="mb-2" controlId="formBasicEmail">
                <Form.Label>Email</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaRegEnvelope />
                  </span>
                  <Form.Control
                    type="email"
                    className="round-right"
                    placeholder="nome@dominio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-2" controlId="formBasicPassword">
                <Form.Label>Palavra-passe</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">
                    <RiLockPasswordLine />
                  </span>
                  <Form.Control
                    type="password"
                    className="round-right"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="current-password"
                    required
                  />
                </div>
              </Form.Group>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="form-check small">
                  <input className="form-check-input" type="checkbox" id="remember" />
                  <label className="form-check-label login-muted" htmlFor="remember">
                    Manter sessão iniciada
                  </label>
                </div>
              </div>

              <Button
                variant="primary"
                className="w-100 mb-2"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    Entrar <PiSignInBold />
                  </>
                )}
              </Button>

              <div className="link-muted text-center">
                Precisas de acesso?{" "}
                <a href="#" onClick={(e) => e.preventDefault()}>
                  Contacta o administrador.
                </a>
              </div>
 
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}

/* Opcional: mantive o SessionTester como estava */
const SessionTester = () => {
  const [numero, setNumero] = useState(null);
  const [mensagem, setMensagem] = useState("");

  const armazenarNumero = async () => {
    try {
      const res = await axios.post(Hoot() + "store-random", {});
      setNumero(res.data.number);
      setMensagem(res.data.msg);
    } catch (error) {
      setMensagem("Erro ao armazenar número.");
      console.error(error);
    }
  };

  const obterNumero = async () => {
    try {
      const res = await axios.get(Hoot() + "all-managers");
      console.log(res.data);
      setNumero(res.data.number);
      setMensagem("Número obtido com sucesso.");
    } catch (error) {
      setMensagem("Erro ao obter número ou número não encontrado.");
      setNumero(null);
      console.error(error);
    }
  };

  useEffect(() => {
    obterNumero();
  }, []);

  return (
    <div>
      <h2>Teste de Sessão</h2>
      <button onClick={armazenarNumero}>Armazenar Número Aleatório</button>
      <button onClick={obterNumero}>Obter Número da Sessão</button>
      {mensagem && <p>{mensagem}</p>}
      {numero !== null && <p>Número: {numero}</p>}
    </div>
  );
};

export default Login;
